import {
  AccessScope,
  CreateQuestionnaireInput,
  QuestionnaireStatus,
} from 'src/graphql';
import { stripHtml } from 'src/utils/stripHtml';
import { LanguageOption } from 'src/utils/languages';
import { FormCategoryMap } from '../hooks/useFormCategories';

function generateInternalName(title: string | null | undefined): string {
  if (!title) return 'untitled-form';
  return (
    title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 100) || 'untitled-form'
  );
}

function getFormCategoryId(
  formType: string | null | undefined,
  formCategoryMap: FormCategoryMap,
): string {
  const key = formType === 'speak-up' ? 'speak-up' : 'conflict-of-interest';
  return formCategoryMap[key];
}

export function mapFormToQuestionnaireInput(
  title: string,
  description: string,
  selectedLanguages: LanguageOption[],
  formType: 'speak-up' | 'conflict-of-interest',
  categoryName: string,
  categoryId: number | null,
  formCategoryMap: FormCategoryMap,
  autoReviewEnabled?: boolean,
  status?: 'active',
  countries?: Array<{ countryCode: string; countryName?: string }>,
  titleTranslations?: Record<string, string>,
  descriptionTranslations?: Record<string, string>,
): CreateQuestionnaireInput {
  const trimmedDescription = description.trim();
  const plainText = stripHtml(trimmedDescription).trim();
  const finalDescription = plainText ? trimmedDescription : null;

  // Build translations array from non-default languages
  const translations = selectedLanguages
    .filter((lang) => lang.value !== 'en-GB') // Exclude default language
    .map((lang) => ({
      languageCode: lang.value,
      languageName: lang.label,
      publicTitle: titleTranslations?.[lang.value] || null,
      description: descriptionTranslations?.[lang.value] || null,
    }))
    .filter((t) => t.publicTitle || t.description); // Only include if has translation

  const baseInput: CreateQuestionnaireInput = {
    internalName: generateInternalName(title),
    publicTitle: title,
    description: finalDescription,
    introduction: finalDescription,
    accessScope: AccessScope.Organization,
    targetDepartmentIds: undefined,
    formCategoryId: getFormCategoryId(formType, formCategoryMap),
    category: categoryName,
    ...(categoryId != null ? { categoryId } : {}),
    languages:
      selectedLanguages.length > 0
        ? selectedLanguages.map((lang) => ({
            languageCode: lang.value,
            languageName: lang.label,
            isDefault: lang.value === 'en-GB',
          }))
        : undefined,
    ...(formType === 'conflict-of-interest'
      ? {
          autoReviewEnabled,
          ...(countries != null ? { countries } : {}),
        }
      : {}),
    ...(translations.length > 0 ? { translations } : {}),
  };

  if (status) {
    return {
      ...baseInput,
      status: QuestionnaireStatus.Active,
    };
  }

  return baseInput;
}
